/*
 * ---------------------------------------------------------------------------------
 * Copyright:
 *      NewtonGreen Technologies Pty. Ltd.
 *      Level 4, 175 Scott St.
 *      Newcastle, NSW, 2300
 *      Australia
 * 
 *      E-mail: support@newtongreen.com
 *      Tel: (02) 4925 5288
 *      Fax: (02) 4925 3068
 * 
 *      All Rights Reserved.
 * ---------------------------------------------------------------------------------
 */

/*
 * --------------------------------------------------------------------------------
 * This file contains the base layout for the browse view.
 * --------------------------------------------------------------------------------
 */

/*
 * ---------------------------------------------------------------------------------
 * Imports - External
 * ---------------------------------------------------------------------------------
 */

/**
 * Required to make use of JSX functionality
 */
import * as React from 'react';

/**
 * Utility used to generate a Hook to allow for the creation of styles using JSS.
 */
import { makeStyles } from '@material-ui/core/styles';

/**
 * Material UI Paper component.
 */
import Paper from '@material-ui/core/Paper';

/**
 * Material UI List component.
 */
import List from '@material-ui/core/List';

/**
 * Material UI Typography component.
 */
import { Typography } from '@material-ui/core';

/**
 * Material UI Breadcrumbs component.
 */
import Breadcrumbs from '@material-ui/core/Breadcrumbs';

/**
 * Material UI Link component.
 */
import Link from '@material-ui/core/Link';

/**
 * Typings
 */
import { RouteChildrenProps} from 'react-router';

/**
 * Used to navigate around the app.
 */
import { Link as RouterLink } from 'react-router-dom';

/*
 * ---------------------------------------------------------------------------------
 * Imports - Internal
 * ---------------------------------------------------------------------------------
 */

/**
 * Enables the use of grade and subterm data loaded from the ServiceStack API.
 */
import useSubtermsAndGrades from 'src/hooks/useSubtermsAndGrades';

/**
 * Enumeration
 */
import { RequestState } from 'src/store/reducers';

/**
 * used to render the view for each grade item.
 */
import GradeListItem from 'src/components/GradeListItem';

/**
 * Loading indicator
 */
import Loading from 'src/components/Loading';

/**
 * Used to limit results to the current version only.
 */
import useVersionCheck from 'src/hooks/useVersionCheck';

/**
 * used to render the view for each term item.
 */
import TermListItem from 'src/components/TermListItem';

import useSubtermsAndGradesIndividualGrade from 'src/hooks/useSubtermsAndGradesIndividualGrade';
import useSubtermsAndGradesIndividualTerm from 'src/hooks/useSubtermsAndGradesIndividualTerm';

/**
 * used to update app state and post messages to opener window.
 */
import useAppState from 'src/hooks/useAppState';

/**
 * Enumeration
 */
import { WindowView } from 'src/utils/MessageManager';
import { useAppSelector, AppSelectors } from 'src/store/reducers/app';
import UnknownGradeListItem from 'src/components/UnknownGradeListItem';

/*
 * ---------------------------------------------------------------------------------
 * Interfaces / Types
 * ---------------------------------------------------------------------------------
 */

interface IBrowseGradesParams {
    termId: string;
}

interface IBrowseGradesProps extends RouteChildrenProps<IBrowseGradesParams> {

}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const useStyles = makeStyles(theme => ({
    title: {
        margin: theme.spacing(3, 0)
    },
    paper: {
        margin: theme.spacing(3, 0)
    },
    list: {
        width: '100%',
        padding: theme.spacing(0,0)
    },
    breadcrumbs: {
        margin: theme.spacing(3, 0)
    }
}));

/*
 * ---------------------------------------------------------------------------------
 * Constants
 * ---------------------------------------------------------------------------------
 */

// const limit = 5;

/*
 * ---------------------------------------------------------------------------------
 * Components
 * ---------------------------------------------------------------------------------
 */

const BrowseGrades: React.FunctionComponent<IBrowseGradesProps> = ({
    match
}) => {
    const classes = useStyles();

    if (!match || !match.params.termId) {
        return null;
    }

    const [ version, category, parentTerm, term, termView, gradeView, loadState ] = useSubtermsAndGrades(parseInt(match.params.termId, 10));

    useVersionCheck(version);

    useAppState(
        WindowView.Browse,
        version,
        category,
        parentTerm ? parentTerm : term,
        parentTerm ? term : null,
        null
    );

    const targetTermId = useAppSelector(AppSelectors.selectTargetTermId);
    const allowUnknownGrade = useAppSelector(AppSelectors.selectAllowUnknownGrade);

    const loading = loadState && loadState.state === RequestState.Pending;

    return (
        <>
            {
                version && category && term ?
                    <Breadcrumbs
                        className={classes.breadcrumbs}
                        separator="›"
                        aria-label="Breadcrumb"
                    >
                        {
                            targetTermId ?
                                <Typography>
                                    Categories
                                </Typography>:
                                <Link
                                    component={RouterLink}
                                    to={`/${version.code}/browse`}
                                >
                                    Categories
                                </Link>
                        }
                        {
                            targetTermId ?
                                <Typography>
                                    {category.name}
                                </Typography>:
                                <Link
                                    component={RouterLink}
                                    to={`/${version.code}/browse/category/${category.id}`}
                                >
                                    {category.name}
                                </Link>
                        }
                        {
                            parentTerm ?
                                targetTermId && targetTermId !== parentTerm.id ?
                                    <Typography>
                                        {parentTerm.name}
                                    </Typography>:
                                    <Link
                                        component={RouterLink}
                                        to={`/${version.code}/browse/term/${parentTerm.id}`}
                                    >
                                        {parentTerm.name}
                                    </Link>:
                                null
                        }
                        <Typography color="textPrimary">
                            {term.name}
                        </Typography>
                    </Breadcrumbs> :
                    <Breadcrumbs
                        className={classes.breadcrumbs}
                        separator="›"
                        aria-label="Breadcrumb"
                    >
                        <Typography>
                            Categories
                        </Typography>
                    </Breadcrumbs>
            }
            <Paper
                className={classes.paper}
            >
                {
                    loading && (
                        <Loading />
                    )
                }
                <List
                    className={classes.list}
                >
                    {
                        version && category && term && termView.map((termId, index) => (
                            <TermListItem
                                key={`st-${termId}`}
                                version={version}
                                category={category}
                                parentTerm={term}
                                termId={termId}
                                showDivider={index !== termId}
                                getTerm={useSubtermsAndGradesIndividualTerm}
                            />
                        ))
                    }
                    {
                        version && category && term && gradeView.map((gradeId, index) => (
                            <GradeListItem
                                key={`g-${gradeId}`}
                                version={version}
                                category={category}
                                term={parentTerm ? parentTerm : term}
                                subterm={parentTerm ? term : null}
                                gradeId={gradeId}
                                showDivider={allowUnknownGrade || index !== gradeId}
                                getGrade={useSubtermsAndGradesIndividualGrade}
                            />
                        ))
                    }
                    {
                        version && category && term && gradeView && gradeView.length > 0 && allowUnknownGrade && (
                            <UnknownGradeListItem
                                version={version}
                                category={category}
                                term={parentTerm ? parentTerm : term}
                                subterm={parentTerm ? term : null}
                                showDivider={false}
                            />
                        )
                    }
                </List>
            </Paper>
        </>
    );
};

/*
 * ---------------------------------------------------------------------------------
 * Exports - Default
 * ---------------------------------------------------------------------------------
 */

export default BrowseGrades;


// WEBPACK FOOTER //
// ./src/containers/BrowseSubtermsAndGrades.tsx